import OSS, { type Options } from 'ali-oss';
import { ImageUtils } from "@/shared/lib/utils/image-utils.ts";
import type { OssUploadCredentials } from './oss-upload-adapter';

type UploadProgressCallback = (percent: number) => void;

interface UploadResult {
    code: number;
    msg: string;
    data?: { key: string };
}

interface OssClient {
    cancel: () => void;
    multipartUpload: (
        key: string,
        file: File,
        options?: {
            partSize?: number;
            progress?: (percent: number) => void | Promise<void>;
        },
    ) => Promise<{ name?: string }>;
}

let uploadQueue: Record<string, { client: OssClient }> = {};

export function cancelAllUpload() {
    Object.keys(uploadQueue).forEach(key => {
        cancelUploadByKey(key);
    });
    uploadQueue = {};
}

function cancelUploadByKey(key: string) {
    const task = uploadQueue[key];
    if (task) {
        task.client.cancel();
        deleteByKey(key);
    }
}

function addIntoKey(key: string, client: OssClient): boolean {
    if (uploadQueue[key]) {
        console.warn(`Key "${key}" already exists in upload queue`);
        return false;
    }

    uploadQueue[key] = { client };
    return true;
}

function deleteByKey(key: string) {
    delete uploadQueue[key];
}

function createOssClient(ossToken: OssUploadCredentials): OssClient {
    const options: Options = {
        accessKeyId: ossToken.aliyun.accessKeyId.trim(),
        accessKeySecret: ossToken.aliyun.accessKeySecret.trim(),
        stsToken: ossToken.aliyun.securityToken.trim(),
        region: ossToken.region.trim(),
        endpoint: ossToken.endpoint,
        bucket: ossToken.bucket,
        cname: false,
        secure: true,
        timeout: 120_000,
    };

    return new OSS(options) as OssClient;
}

async function dataUrlToFile(dataUrl: string, key: string): Promise<File | null> {
    try {
        const response = await fetch(dataUrl);
        if (!response.ok) {
            return null;
        }

        const blob = await response.blob();
        return new File([blob], key, { type: blob.type || 'application/octet-stream', lastModified: Date.now() });
    } catch {
        return null;
    }
}

function getOssErrorMessage(error: unknown) {
    if (error && typeof error === 'object') {
        const record = error as { message?: string; name?: string };
        if (record.name === 'cancel' || record.message === 'cancel') {
            return '上传已取消';
        }

        if (record.message?.includes('timeout')) {
            return '上传失败，请检查网络设置后重试';
        }

        if (record.message === 'Network Error') {
            return '上传失败，请检查网络设置后重试';
        }

        if (record.message) {
            return '上传失败，请检查网络设置后重试';
        }
    }

    return '上传失败，请检查网络设置后重试';
}

export async function uploadFile(file: File, key: string, ossToken: OssUploadCredentials, progressCb?: UploadProgressCallback, abortSignal?: AbortSignal): Promise<UploadResult> {
    const client = createOssClient(ossToken);

    if (abortSignal?.aborted) {
        return { code: -1, msg: '上传已取消' };
    }

    addIntoKey(key, client);
    const abortHandler = () => client.cancel();
    abortSignal?.addEventListener('abort', abortHandler, { once: true });

    try {
        const result = await client.multipartUpload(key, file, {
            partSize: 1024 * 1024,
            progress(percent) {
                progressCb?.(Math.floor(percent * 100));
            },
        });
        return { code: 1, msg: 'ok', data: { key: result.name || key } };
    } catch (error) {
        return { code: -1, msg: getOssErrorMessage(error) };
    } finally {
        abortSignal?.removeEventListener('abort', abortHandler);
        deleteByKey(key);
    }
}

export async function uploadForBase64(base64Img: string, key: string, ossToken: OssUploadCredentials, progressCb?: UploadProgressCallback, abortSignal?: AbortSignal) {
    const format = ImageUtils.getBase64ImageFormat(base64Img);
    if (format && ['jpg', 'png', 'jpeg'].indexOf(format) == -1) {
        const file = await ImageUtils.base64ToPngFile(base64Img, key);
        if (!file) {
            return { code: -1, msg: '图片转换失败' };
        }

        return uploadFile(file, key, ossToken, progressCb, abortSignal);
    }

    const file = await dataUrlToFile(base64Img, key);
    if (!file) {
        return { code: -1, msg: '图片转换失败' };
    }

    return uploadFile(file, key, ossToken, progressCb, abortSignal);
}

export async function uploadForText(text: string, key: string, ossToken: OssUploadCredentials, progressCb?: UploadProgressCallback, abortSignal?: AbortSignal) {
    const encoder = new TextEncoder();
    const encodedText = encoder.encode(text);
    const blob = new Blob([encodedText], { type: 'text/plain;charset=UTF-8' });
    const file = new File([blob], key, { type: 'text/plain;charset=UTF-8', lastModified: Date.now() });
    return uploadFile(file, key, ossToken, progressCb, abortSignal);
}
